import { t } from "@lingui/macro";
import { RouteProp, useRoute } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format } from "date-fns";
import { useAtom } from "jotai";
import { useCallback, useEffect, useState } from "react";
import { useFragment } from "react-relay";

import {
  facilityToScheduleOption,
  ScheduleOption,
} from "@/components/ScheduleSwitcher";
import { ScheduleSwitcher } from "@/components/ScheduleSwitcher/ScheduleSwitcher";
import { useFacilityStartEndTime } from "@/features/Facility";
import { useFacilityGroupSize } from "@/features/Facility/hooks/useFacilityGroupSize";
import { useScheduleNavigator } from "@/hooks/useScheduleNavigator";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { ScreenFooter } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenFooter";
import { Button } from "@whoosh/design-system/src/components/controls";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  Box,
  HStack,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import {
  TimeOfDayFilterControl,
  GroupSizeFilterControl,
} from "../../../../components/AgendaFilters";
import {
  AgendaQueryInputs,
  agendaQueryInputsAtom,
} from "../../atoms/agendaQueryInputs";
import {
  CourseBucketFilterControl,
  DateSelectorControl,
  DurationFilterControl,
} from "../DurationBasedFilters";
import { DurationBasedFiltersModalFragment_facility$key } from "./__generated__/DurationBasedFiltersModalFragment_facility.graphql";
import { DurationBasedFiltersModal_session$key } from "./__generated__/DurationBasedFiltersModal_session.graphql";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  facilityRef: DurationBasedFiltersModalFragment_facility$key | null;
  sessionRef: DurationBasedFiltersModal_session$key | null;
};

export const DurationBasedFiltersModal = ({
  isOpen,
  onClose,
  facilityRef,
  sessionRef,
}: Props) => {
  const route = useRoute<RouteProp<ClubStackNavigatorParamList, "Agenda">>();

  const date = route?.params?.date;

  const session = useFragment(
    DurationBasedFiltersModalSessionFragmentString,
    sessionRef
  );
  const facility = useFragment(
    DurationBasedFiltersModalFragmentString,
    facilityRef
  );
  const facilityStartEndTime = useFacilityStartEndTime(facility ?? null);
  const facilityGroupSizes = useFacilityGroupSize(facility ?? null);

  const [globalFilterState, setGlobalFilterState] = useAtom(
    agendaQueryInputsAtom
  );

  const [localFilterState, setLocalFilterState] = useState<AgendaQueryInputs>({
    ...globalFilterState,
  });

  useEffect(() => {
    if (isOpen) {
      setLocalFilterState({ ...globalFilterState });
    }
  }, [globalFilterState, isOpen]);

  const onSave = useCallback(() => {
    setGlobalFilterState(localFilterState);
    onClose();
  }, [localFilterState, onClose, setGlobalFilterState]);

  const onReset = useCallback(() => {
    setLocalFilterState({});
  }, []);

  const onGroupSizeChange = useCallback((value: number | undefined) => {
    setLocalFilterState((prev) => {
      return {
        ...prev,
        groupSize: value,
      };
    });
  }, []);

  const onDurationChange = useCallback((value: number) => {
    setLocalFilterState((prev) => {
      return {
        ...prev,
        duration: value,
      };
    });
  }, []);

  const onCourseBucketChange = useCallback((value: (string | null)[]) => {
    setLocalFilterState((prev) => {
      return {
        ...prev,
        courseBucketIds: value.flatMap((v) => (v ? [v] : [])),
      };
    });
  }, []);

  const onTimeOfDayChange = useCallback(
    ({ timeLTE, timeGTE }: { timeLTE?: string; timeGTE?: string }) => {
      if (!timeLTE && !timeGTE) {
        setLocalFilterState((prev) => {
          return {
            ...prev,
            times: undefined,
          };
        });
      } else {
        setLocalFilterState((prev) => {
          return {
            ...prev,
            times: {
              timeLTE,
              timeGTE,
            },
          };
        });
      }
    },
    [setLocalFilterState]
  );

  const navigateToSchedule = useScheduleNavigator();

  const onScheduleSwitcherChange = (option: ScheduleOption) => {
    const curDate = new Date();
    const todayString = format(curDate, "yyyy-MM-dd");
    const navigateDate = date ? date : todayString;
    navigateToSchedule(option.type, option.slug, navigateDate);
  };

  const selectedScheduleOption = facilityToScheduleOption(facility ?? null);

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      upperTitle={t`Filters`}
      hideFooterPadding
      content={
        <VStack px={6} flex={1} gap={6}>
          <ScheduleSwitcher
            selectedOption={selectedScheduleOption}
            onChange={onScheduleSwitcherChange}
            sessionRef={session ?? null}
          />
          <DurationFilterControl
            facilityRef={facility ?? null}
            selectedDuration={localFilterState.duration}
            onChange={onDurationChange}
          />
          <TimeOfDayFilterControl
            controlStartTime={facilityStartEndTime.startTime}
            controlEndTime={facilityStartEndTime.endTime}
            selectedTimeRange={localFilterState.times}
            onChange={onTimeOfDayChange}
          />
          <GroupSizeFilterControl
            selectedGroupSize={localFilterState.groupSize}
            onChange={onGroupSizeChange}
            controlMinGroupSize={facilityGroupSizes.min}
            controlMaxGroupSize={facilityGroupSizes.max}
          />
          <CourseBucketFilterControl
            facilityRef={facility ?? null}
            selectedCourseBucketIds={localFilterState.courseBucketIds}
            onChange={onCourseBucketChange}
          />
          <DateSelectorControl facilityRef={facility ?? null} />
        </VStack>
      }
      footerContent={
        <ScreenFooter>
          <HStack flex={1} justifyContent="space-between">
            <Box alignSelf="flex-start">
              <Button
                onPress={onReset}
                variant="ghost"
                accessibilityLabel={t`Reset button`}
              >{t`Reset`}</Button>
            </Box>
            <Box alignSelf="flex-end">
              <Button
                onPress={onSave}
                accessibilityLabel={t`Save and Close button`}
              >{t`Save and Close`}</Button>
            </Box>
          </HStack>
        </ScreenFooter>
      }
    />
  );
};

export const DurationBasedFiltersModalFragmentString = graphql`
  fragment DurationBasedFiltersModalFragment_facility on Facility {
    ...useFacilityStartEndTime_facility
    ...useFacilityGroupSize_facility
    ...CourseBucketFilterControlFragment_facility
    ...DurationFilterControlFragment_facility
    ...DateSelectorControlFragment_facility
    ...facilityToScheduleOption_facility
  }
`;

const DurationBasedFiltersModalSessionFragmentString = graphql`
  fragment DurationBasedFiltersModal_session on Session {
    ...ScheduleSwitcher_session
  }
`;
